import { Navigate, NavLink, useParams } from 'react-router';

import { PageContainer } from '@/components/landing/sections';
import { cn } from '@/lib/utils';
import { findHelpArticle, HELP_GROUPS, helpArticlesOf } from './articles';
import { type HelpChromeProps, HelpLayout } from './HelpLayout';

/**
 * 記事 1 枚。
 *
 * 知らない slug は索引へ戻す (replace)。履歴に残すと、戻るボタンで
 * 同じ 404 に着地し続ける。
 */
export function HelpArticlePage({ signedIn, home }: HelpChromeProps) {
  const { slug } = useParams();
  const article = findHelpArticle(slug);

  if (!article) return <Navigate to="/help" replace />;

  const { Component } = article;

  return (
    <HelpLayout signedIn={signedIn} home={home}>
      <PageContainer className="pt-8 pb-14 sm:pt-12 sm:pb-20">
        <div className="grid gap-8 sm:gap-12 lg:grid-cols-[15rem_minmax(0,1fr)]">
          <HelpNav />

          <article className="flex min-w-0 flex-col gap-6 sm:gap-8">
            <header className="flex flex-col gap-3">
              <span className="font-mono text-[11px] tracking-wider text-ink-faint">
                {article.slug}
              </span>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{article.title}</h1>
              <p className="text-sm leading-relaxed text-ink-muted">{article.summary}</p>
            </header>

            <Component />
          </article>
        </div>
      </PageContainer>
    </HelpLayout>
  );
}

/**
 * 記事ナビ。
 *
 * lg 未満では本文の上に畳む。読み手の固まりが分かれば十分なので、
 * 折りたたみの開閉は持たない。
 *
 * **640px 未満だけは本文の後ろに回す** (`order-last`)。8 本のリンクは
 * 手のひらの幅で 1 画面ぶんの高さになり、開いた記事の見出しが最初の画面から
 * 押し出される。索引は `/help` にもあるので、記事の中では「読み終えた次に
 * 選ぶもの」として下に置く方が順序として正しい。折りたたみの開閉は
 * ここでも持たない (部品が増えるうえ、閉じている間は現在地も消える)。
 */
function HelpNav() {
  return (
    <nav className="order-last flex flex-col gap-6 self-start sm:order-none lg:sticky lg:top-8">
      {HELP_GROUPS.map((group) => (
        <div key={group.audience} className="flex flex-col gap-1">
          <span className="px-2.5 py-2 font-mono text-[11px] tracking-wider text-ink-faint">
            {group.eyebrow}
          </span>
          {helpArticlesOf(group.audience).map((article) => (
            <NavLink
              key={article.slug}
              to={`/help/${article.slug}`}
              className={({ isActive }) =>
                cn(
                  'px-2.5 py-2 text-sm',
                  isActive ? 'bg-brand-700 text-surface-raised' : 'text-ink-muted hover:text-ink',
                )
              }
            >
              {article.title}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}
